import { Injectable, OnDestroy } from '@angular/core';
import { interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RemindersService } from './reminders.service';
import { ReminderSchedulerService } from './reminder-scheduler.service';

@Injectable({
  providedIn: 'root'
})
export class ReminderAlertService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private syncInterval = 300000; // 5 minutes background sync

  constructor(
    private remindersService: RemindersService,
    private schedulerService: ReminderSchedulerService
  ) {}

  ngOnDestroy(): void {
    this.stopSync();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize the smart scheduler system
   * Call this in App Component ngOnInit
   */
  initialize(): void {
    // Only initialize if user is authenticated
    if (localStorage.getItem('_isLoggedIn') !== 'true') {
      return;
    }
    console.log('[ReminderAlert] Initializing smart scheduler system...');

    // Load reminders and schedule alerts
    this.loadAndSchedule();

    // Start background sync every 5 minutes
    this.startBackgroundSync();
  }

  /**
   * Load reminders from backend and schedule all alerts
   */
  private loadAndSchedule(): void {
    console.log('[ReminderAlert] Loading reminders from backend...');

    this.remindersService.loadFromBackend();

    // Subscribe to reminders changes and reschedule
    this.remindersService.reminders$
      .pipe(takeUntil(this.destroy$))
      .subscribe(reminders => {
        console.log(`[ReminderAlert] Received ${reminders.length} reminders, scheduling alerts...`);
        this.schedulerService.scheduleAllReminders(reminders);
      });
  }

  /**
   * Start background sync to pick up new reminders and status changes
   */
  private startBackgroundSync(): void {
    console.log(`[ReminderAlert] Starting background sync every ${this.syncInterval / 1000} seconds...`);

    interval(this.syncInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        console.log('[ReminderAlert] Background sync: Reloading reminders...');
        this.remindersService.loadFromBackend();
        // Reminders will be rescheduled automatically via reminders$ subscription
      });
  }

  /**
   * Stop background sync
   */
  stopSync(): void {
    console.log('[ReminderAlert] Stopping background sync...');
    this.destroy$.next();
  }

  /**
   * Manually trigger a sync (useful for testing or after user action)
   */
  syncNow(): void {
    console.log('[ReminderAlert] Manual sync triggered...');
    this.remindersService.loadFromBackend();
  }

  /**
   * Reset all timers and shown tracking
   * Useful when user logs out or manually refreshes
   */
  reset(): void {
    console.log('[ReminderAlert] Resetting scheduler...');
    this.schedulerService.clearAllTimers();
    this.schedulerService.resetShownReminders();
  }

  /**
   * Get sync interval in milliseconds
   */
  getSyncInterval(): number {
    return this.syncInterval;
  }

  /**
   * Set sync interval (in milliseconds)
   */
  setSyncInterval(intervalMs: number): void {
    this.syncInterval = Math.max(60000, intervalMs); // Minimum 1 minute
    console.log(`[ReminderAlert] Sync interval set to ${this.syncInterval}ms`);
  }

  /**
   * Get number of currently scheduled reminders (for debugging)
   */
  getScheduledCount(): number {
    return this.schedulerService.getScheduledCount();
  }
}
